/**
 * Tour runtime helpers — shared between TrainingContext (real tours)
 * and TrainingScope (preview tours during authoring).
 */

import type { TrainingAction } from './types';

/** Max wait when a single lookup has the full budget to itself. */
export const ELEMENT_WAIT_TIMEOUT_MS = 2000;
/**
 * Hard cap on beforeShow work (action chain + attach target) per tour step.
 * Per-action waits use the remaining budget, not the full ELEMENT_WAIT_TIMEOUT_MS.
 */
export const TOUR_STEP_PREPARE_BUDGET_MS = 2000;
/** Settle delay between chained actions so React can re-render. */
const ACTION_SETTLE_MS = 120;

/**
 * Built-in attach fallbacks for targets that are often missing (e.g. no
 * contract-device slots). Step-level `fallbackTargets` are tried between
 * the primary target and these defaults.
 */
const BUILTIN_TARGET_FALLBACKS: Record<string, string[]> = {
  'devices-tree-available': ['devices-tree-assigned', 'devices-tree-unassigned'],
  'devices-available-slot': [
    'devices-tree-contract-device',
    'devices-tree-device',
    'devices-tree-assigned',
    'devices-tree-unassigned',
  ],
  'tree-menu-createDeviceFromSlot': [
    'tree-menu-editDevice',
    'tree-menu-configureDevice',
    'devices-tree-device',
  ],
  'device-setup-wizard': [
    'devices-tree-device',
    'devices-tree-assigned',
    'devices-tree-unassigned',
    'devices-admin-contract-device-configurations',
  ],
  'device-setup-details-step': [
    'device-setup-wizard',
    'devices-tree-device',
  ],
};

function tourIdSelector(tourId: string): string {
  return `[data-tour-id="${tourId}"]`;
}

function isTourElementVisible(el: HTMLElement): boolean {
  if (el.style.display === 'none') return false;
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

/** Prefer a visible match when many nodes share the same tour id (e.g. device rows). */
export function findBestTourElement(tourId: string): HTMLElement | null {
  const nodes = document.querySelectorAll(tourIdSelector(tourId));
  for (const node of nodes) {
    if (node instanceof HTMLElement && isTourElementVisible(node)) return node;
  }
  return null;
}

export function tourTargetCandidates(
  target: string,
  fallbackTargets?: string[],
): string[] {
  const builtins = BUILTIN_TARGET_FALLBACKS[target] ?? [];
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const id of [target, ...(fallbackTargets ?? []), ...builtins]) {
    if (seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  return ordered;
}

/** Resolve a tour target for an action or attach step, trying fallbacks before waiting. */
async function resolveTourTargetElement(
  target: string,
  fallbackTargets: string[] | undefined,
  budgetMs: number,
): Promise<HTMLElement | null> {
  const candidates = tourTargetCandidates(target, fallbackTargets);
  for (let i = 0; i < candidates.length; i++) {
    const id = candidates[i];
    const immediate = findBestTourElement(id);
    if (immediate) return immediate;

    const slotsLeft = candidates.length - i;
    const slice = Math.max(50, Math.ceil(budgetMs / slotsLeft));
    await waitForElement(tourIdSelector(id), slice);
    // waitForElement resolves via querySelector (first match, visibility
    // ignored). Re-check with findBestTourElement so a hidden ghost row
    // (collapsed/virtualized tree) never becomes the action target.
    const el = findBestTourElement(id);
    if (el) return el;
  }
  return null;
}

const ATTACH_RESERVE_MS = 400;

export async function resolveTourAttachElement(
  target: string,
  before: TrainingAction[] | undefined,
  options: { getTimeoutMs: () => number },
  fallbackTargets?: string[],
): Promise<HTMLElement | null> {
  if (before?.length) {
    await runActionChain(before, {
      getTimeoutMs: () => Math.max(0, options.getTimeoutMs() - ATTACH_RESERVE_MS),
    });
  }

  const candidates = tourTargetCandidates(target, fallbackTargets);

  for (const id of candidates) {
    const el = findBestTourElement(id);
    if (el) return el;
  }

  for (let i = 0; i < candidates.length; i++) {
    const remaining = options.getTimeoutMs();
    if (remaining <= 0) break;
    const slotsLeft = candidates.length - i;
    const slice = Math.max(50, Math.ceil(remaining / slotsLeft));
    await waitForElement(tourIdSelector(candidates[i]), slice);
    const el = findBestTourElement(candidates[i]);
    if (el) return el;
  }

  return null;
}

export type TourStepPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/** Mutable attach target shared with Shepherd step options (see bindTourStepBeforeShow). */
export interface TourStepAttachHost {
  attachTo: {
    // Shepherd's StepOptionsAttachTo types element as string | HTMLElement, so
    // keep this HTMLElement (not the broader Element that querySelector returns)
    // to stay assignable at the tour.addStep call sites.
    element: string | HTMLElement;
    on: TourStepPlacement;
  };
}

export function createTourStepAttachHost(
  target: string,
  placement?: TourStepPlacement,
): TourStepAttachHost {
  return {
    attachTo: {
      element: `[data-tour-id="${target}"]`,
      on: placement || 'auto',
    },
  };
}

/**
 * Shepherd `beforeShowPromise` — runs the action chain, resolves the best
 * attach element (primary target or fallback), and writes into `attachHost`.
 *
 * Shepherd 15 invokes `options.beforeShowPromise()` with no step `this`, so
 * do not call `updateStepOptions` here; mutate the shared `attachTo` object
 * passed to `tour.addStep` instead.
 */
export function bindTourStepBeforeShow(
  step: {
    target: string;
    before?: TrainingAction[];
    fallbackTargets?: string[];
    placement?: TourStepPlacement;
  },
  attachHost: TourStepAttachHost,
): () => Promise<void> {
  return async function tourStepBeforeShow() {
    const deadline = Date.now() + TOUR_STEP_PREPARE_BUDGET_MS;
    const el = await resolveTourAttachElement(
      step.target,
      step.before,
      { getTimeoutMs: () => remainingBudgetMs(deadline) },
      step.fallbackTargets,
    );
    if (el) {
      attachHost.attachTo.element = el as HTMLElement;
      attachHost.attachTo.on = step.placement || 'auto';
    }
  };
}

function remainingBudgetMs(deadline: number): number {
  return Math.max(0, deadline - Date.now());
}

/**
 * Run a step's `before` chain and resolve its attach target, bounded by
 * {@link TOUR_STEP_PREPARE_BUDGET_MS}. Returns the matched element, if any.
 */
export async function prepareTourStep(
  target: string,
  before?: TrainingAction[],
  fallbackTargets?: string[],
): Promise<HTMLElement | null> {
  const deadline = Date.now() + TOUR_STEP_PREPARE_BUDGET_MS;
  return resolveTourAttachElement(
    target,
    before,
    { getTimeoutMs: () => remainingBudgetMs(deadline) },
    fallbackTargets,
  );
}

interface RunActionChainOptions {
  /** Remaining ms for the next wait; defaults to {@link ELEMENT_WAIT_TIMEOUT_MS}. */
  getTimeoutMs?: () => number;
}

/**
 * Resolve when an element matching `selector` exists, or timeout.
 * Returns the element if found, or null on timeout.
 */
export function waitForElement(selector: string, timeoutMs: number): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeoutMs);
  });
}

/**
 * Walk a pre-step action chain. Each action waits for its target, dispatches
 * the corresponding event, and yields a short settle so the next action can
 * find DOM the previous one revealed (e.g. context menu items after right-click).
 */
export async function runActionChain(
  actions: TrainingAction[],
  options?: RunActionChainOptions,
): Promise<void> {
  const getTimeoutMs = options?.getTimeoutMs ?? (() => ELEMENT_WAIT_TIMEOUT_MS);

  for (const action of actions) {
    const budget = getTimeoutMs();
    if (budget <= 0) break;

    const actionFallbacks = 'fallbackTargets' in action
      ? action.fallbackTargets
      : undefined;
    const timeout = action.type === 'wait' && action.timeoutMs
      ? Math.min(action.timeoutMs, budget)
      : budget;
    const el = await resolveTourTargetElement(
      action.target,
      actionFallbacks,
      timeout,
    );
    if (!el) continue;

    switch (action.type) {
      case 'click':
        el.click();
        break;
      case 'rightClick':
        el.dispatchEvent(new MouseEvent('contextmenu', {
          bubbles: true, cancelable: true, button: 2, buttons: 2,
        }));
        break;
      case 'hover':
        el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true }));
        el.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true, cancelable: true }));
        break;
      case 'wait':
        // already satisfied by waitForElement above
        break;
    }
    if (action.type !== 'wait') {
      await new Promise((r) => setTimeout(r, ACTION_SETTLE_MS));
    }
  }
}
